import { computed } from 'vue';
import {
  employmentReferenceApi,
  fetchEmploymentReferenceOptions,
  invalidateEmploymentReferenceOptions,
} from '@/api/employmentReferenceApi';
import {
  errorRef,
  fallbackSnapshot,
  findCompanyByName,
  findTypeByCode,
  getCatalogCompanyOptionsForEmploymentType,
  getCatalogEmploymentTypeOptions,
  loadingRef,
  mapCompanyOption,
  mapTypeOption,
  normalizeCode,
  snapshotRef,
} from '@/composables/employmentReferenceCatalogState';

export {
  getCatalogCompanyOptionsForEmploymentType,
  getCatalogEmploymentTypeOptions,
  resetEmploymentReferenceCatalogSnapshot,
} from '@/composables/employmentReferenceCatalogState';

/**
 * API-backed employment types + employing companies catalog.
 */
export function useEmploymentReferenceCatalog() {
  const employmentTypes = computed(
    () => snapshotRef.value?.employment_types || fallbackSnapshot().employment_types
  );
  const companies = computed(
    () => snapshotRef.value?.companies || fallbackSnapshot().companies
  );
  const loading = computed(() => loadingRef.value);
  const error = computed(() => errorRef.value);

  async function ensureLoaded({ force = false } = {}) {
    if (!force && snapshotRef.value) return snapshotRef.value;
    loadingRef.value = true;
    errorRef.value = '';
    try {
      const data = await fetchEmploymentReferenceOptions({ force });
      snapshotRef.value = data;
      return data;
    } catch (err) {
      errorRef.value = err?.message || 'Failed to load employment reference options';
      return fallbackSnapshot();
    } finally {
      loadingRef.value = false;
    }
  }

  async function refresh() {
    invalidateEmploymentReferenceOptions();
    return ensureLoaded({ force: true });
  }

  async function createType(name) {
    const code = normalizeCode(name);
    if (!code) throw new Error('Employment type is required');
    const { type } = await employmentReferenceApi.types.create({ code, label: code });
    await refresh();
    return mapTypeOption(type);
  }

  async function updateType(currentValue, name) {
    const existing = findTypeByCode(currentValue);
    if (!existing?.id) throw new Error('Employment type not found');
    const code = normalizeCode(name);
    if (!code) throw new Error('Employment type is required');
    const { type } = await employmentReferenceApi.types.update({
      id: existing.id,
      code,
      label: code,
    });
    await refresh();
    return { ...mapTypeOption(type), previousValue: existing.code };
  }

  async function deleteType(value) {
    const existing = findTypeByCode(value);
    if (!existing?.id) throw new Error('Employment type not found');
    await employmentReferenceApi.types.delete({ id: existing.id });
    await refresh();
    return existing.code;
  }

  async function createCompany(name, employmentType) {
    const label = normalizeCode(name);
    const typeCode = normalizeCode(employmentType);
    if (!label) throw new Error('Company name is required');
    if (!typeCode) throw new Error('Select employment type first');
    const { company } = await employmentReferenceApi.companies.create({
      name: label,
      employment_type: typeCode,
    });
    await refresh();
    return mapCompanyOption(company);
  }

  async function updateCompany(currentValue, name, employmentType) {
    const existing = findCompanyByName(currentValue);
    if (!existing?.id) throw new Error('Company not found');
    const label = normalizeCode(name);
    const typeCode = normalizeCode(employmentType);
    if (!label) throw new Error('Company name is required');
    if (!typeCode) throw new Error('Select employment type first');
    const { company } = await employmentReferenceApi.companies.update({
      id: existing.id,
      name: label,
      employment_type: typeCode,
    });
    await refresh();
    return {
      ...mapCompanyOption(company),
      previousValue: existing.name,
    };
  }

  async function deleteCompany(value) {
    const existing = findCompanyByName(value);
    if (!existing?.id) throw new Error('Company not found');
    await employmentReferenceApi.companies.delete({ id: existing.id });
    await refresh();
    return existing.name;
  }

  function isManagedType(value) {
    return Boolean(findTypeByCode(value)?.id);
  }

  function isManagedCompany(value) {
    return Boolean(findCompanyByName(value)?.id);
  }

  return {
    employmentTypes,
    companies,
    loading,
    error,
    ensureLoaded,
    refresh,
    createType,
    updateType,
    deleteType,
    createCompany,
    updateCompany,
    deleteCompany,
    isManagedType,
    isManagedCompany,
    getTypeOptions: getCatalogEmploymentTypeOptions,
    getCompanyOptionsForType: getCatalogCompanyOptionsForEmploymentType,
  };
}
